<template>
    <v-card ref="root" flat color="transparent" class="datetime-picker">
        <v-defaults-provider :defaults="defaultProps">
            <v-card-text class="pa-0">
                <v-row dense>
                    <!-- timepicker -->
                    <v-col cols="6" md="5">
                        <time-picker
                            v-bind="props"
                            v-model="timeValue"
                            v-model:focused="timeIsFocused"
                            format="24hr"
                            :min="_minTime"
                            :max="_maxTime"
                            :input-class="timeIsFocused ? activeClass : baseClass"
                            hide-details
                        />
                    </v-col>
                    <!-- datepicker -->
                    <v-col cols="6" md="7">
                        <date-picker
                            v-bind="props"
                            v-model="dateValue"
                            v-model:focused="dateIsFocused"
                            :min="_minDate"
                            :max="_maxDate"
                            :calendar-width="calendarWidth"
                            :input-class="dateIsFocused ? activeClass : baseClass"
                            hide-details
                        />
                    </v-col>
                </v-row>
            </v-card-text>
        </v-defaults-provider>
    </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import { formatDate, isSameDay, isValid, set, startOfDay, toDate } from 'date-fns';
import { useDisplay } from 'vuetify';
import type { VCard, VTextField } from 'vuetify/components';
import { TimePicker } from '../TimePicker';
import { DatePicker } from '../DatePicker';
import type { BaseInputComponentProps } from '../BaseInputComponent';

interface Props extends Omit<BaseInputComponentProps, 'label' | 'placeholder' | 'inputClass' | 'hideDetails'> {
    /**
     * The minimum selectable date
     */
    minDate?: Date | number;

    /**
     * The maximum selectable date
     */
    maxDate?: Date | number;

    /**
     * Time increment/decrement step in minutes
     * @default 30
     */
    timeStepInMinutes?: number;

    /**
     * Background color of the input fields
     */
    bgColor?: string;

    /**
     * CSS class to apply to the base element
     */
    baseClass?: string;

    /**
     * CSS class to apply when the component is active
     */
    activeClass?: string;
}

const props = withDefaults(defineProps<Props>(), { timeStepInMinutes: 30 });
const modelValue = defineModel<Date | number>('modelValue', { default: () => new Date() });
const _minDate = computed(() => (props.minDate && isValid(props.minDate) ? formatDate(props.minDate, 'yyyy-MM-dd') : undefined));
const _maxDate = computed(() => (props.maxDate && isValid(props.maxDate) ? formatDate(props.maxDate, 'yyyy-MM-dd') : undefined));
const _minTime = computed(() =>
    props.minDate && isValid(props.minDate) && isSameDay(props.minDate, modelValue.value) ? formatDate(props.minDate, 'HH:mm') : undefined
);
const _maxTime = computed(() =>
    props.maxDate && isValid(props.maxDate) && isSameDay(props.maxDate, modelValue.value) ? formatDate(props.maxDate, 'HH:mm') : undefined
);
const { mobile: isMobile } = useDisplay();

const defaultProps = computed(() => ({
    VTextField: {
        // hideDetails: true,
        rounded: props.rounded
    },
    VCard: {
        rounded: true
    }
}));
const dateIsFocused = ref(false);
const timeIsFocused = ref(false);
const root = ref<typeof VCard>();
const rootElement = computed(() => root.value?.$el || null);
const { width: rootElementWidth } = useElementSize(rootElement);
const datePickerInput = ref<typeof VTextField>();
const { width: datePickerInputWidth } = useElementSize(datePickerInput.value?.$el, undefined, { box: 'border-box' });
const calendarWidth = computed(() => (isMobile.value ? rootElementWidth.value : Math.max(360, datePickerInputWidth.value)));

const dateValue = computed({
    get() {
        return startOfDay(modelValue.value);
    },
    set(value) {
        modelValue.value = set(modelValue.value, {
            year: value.getFullYear(),
            month: value.getMonth(),
            date: value.getDate()
        });
    }
});

const timeValue = computed({
    get() {
        return modelValue.value;
    },
    set(value) {
        const parsedDate = toDate(value);
        if (isValid(parsedDate)) {
            modelValue.value = set(modelValue.value, {
                hours: parsedDate.getHours(),
                minutes: parsedDate.getMinutes(),
                seconds: 0,
                milliseconds: 0
            });
        }
    }
});
</script>

<style lang="scss">
.datetime-picker {
    overflow: visible;
}
</style>
